import Select from 'react-select';
import { MAPPING_CATEGORIES } from '../constants/mappingConstants';
import InfoTooltip from './InfoTooltip';
import { filterNote, sampleValues } from '../utils/sheetMapping';

// The mapping of one sheet, as on the upload screen. The reprocess dialog uses the same
// block, so a user fixing a mapping finds the screen they mapped the file with.

// The menus are portaled to <body>: without this they open behind the reprocess dialog (z-50)
const selectStyles = { menuPortal: (base) => ({ ...base, zIndex: 9999 }) };

export function ProcessingSwitch({ checked, onChange }) {
  return (
    <div className="flex items-center justify-between px-3 py-2 bg-gray-50 dark:bg-gray-800 rounded-lg">
      <span className="text-base font-medium text-gray-900 dark:text-white">
        Traiter cette feuille
      </span>
      <button
        type="button"
        onClick={onChange}
        className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent
                  transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
                  dark:focus:ring-offset-gray-900 ${checked ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-700'}`}
        role="switch"
        aria-checked={checked}
        aria-label="Traiter cette feuille"
      >
        <span
          className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0
                    transition duration-200 ease-in-out ${checked ? 'translate-x-5' : 'translate-x-0'}`}
        />
      </button>
    </div>
  );
}

// Example values of the chosen column, and for a filter field the week or year it keeps
function ColumnHint({ field, column, sampleRows, period }) {
  if (!column) return null;
  const values = sampleValues(sampleRows, column).join(' · ');
  const note = filterNote(field, period);
  if (!values && !note) return null;
  return (
    <span className="mt-0.5 truncate text-xs text-gray-500 dark:text-gray-400" title={values}>
      {note && <span className="font-medium text-gray-700 dark:text-gray-200">{note} </span>}
      {note && values ? `(fichier : ${values})` : values}
    </span>
  );
}

// mapping is { excel column: field }; onChange(field, column) maps a field, or clears it with an empty column
function MappingFields({ choices, columns, mapping, onChange, disabled, sampleRows, period }) {
  const categorizedMappings = {
    required: choices.filter(m => m.required),
    important: choices.filter(m => m.important && !m.required),
    optional: choices.filter(m => !m.important && !m.required)
  };
  const columnOptions = (columns || []).map(column => ({ value: column, label: column }));
  const columnOf = (field) => Object.entries(mapping).find(([, val]) => val === field)?.[0];
  const isOptionDisabled = (option, field) => Object.keys(mapping).includes(option.value) && mapping[option.value] !== field;
  // In the menu, a column taken by another field names that field, e.g. "GENCOD → ean"
  const optionLabel = (option, { context }, field) => (
    context === 'menu' && isOptionDisabled(option, field) ? `${option.label} → ${mapping[option.value]}` : option.label
  );

  return (
    <div className="grid grid-cols-3 gap-4">
      {Object.entries(MAPPING_CATEGORIES).map(([category, config]) => (
        <div
          key={category}
          className={`rounded-lg border p-4 ${config.bgColor} ${config.borderColor}`}
        >
          <div className="mb-4">
            <h3 className={`text-lg font-medium dark:text-white ${config.titleColor}`}>
              {config.title}
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {config.description}
            </p>
          </div>

          <div className="space-y-3">
            {categorizedMappings[category].map((choice) => {
              const column = columnOf(choice.value);
              return (
                <div key={choice.value} className="flex flex-col">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
                    {choice.value}
                    <InfoTooltip description={choice.description} />
                  </label>
                  <Select
                    value={column ? { value: column, label: column } : null}
                    onChange={(option) => onChange(choice.value, option?.value || '')}
                    options={columnOptions}
                    isDisabled={disabled}
                    isOptionDisabled={(option) => isOptionDisabled(option, choice.value)}
                    formatOptionLabel={(option, meta) => optionLabel(option, meta, choice.value)}
                    isClearable
                    placeholder="Sélectionner une colonne"
                    noOptionsMessage={() => "Aucune colonne disponible"}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    styles={selectStyles}
                    className={category === 'required' && !column ? 'react-select-error' : ''}
                  />
                  <ColumnHint field={choice.value} column={column} sampleRows={sampleRows} period={period} />
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

export default MappingFields;
